import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './LoginScreen.module.css';
import { User } from '../../types';
import { UserService } from '../../api/services/UserService';
import { useTranslation } from '../../i18n/provider';

interface LoginScreenProps {
  onDemoLogin: () => void;
  onLogin: (user: User, token: string) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onDemoLogin, onLogin }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // TODO.md J1: ResetPasswordScreen navigates here with this flag after a successful password
  // reset. Read once at mount-time render, not persisted - a later re-render (e.g. after a failed
  // login attempt sets `error`) does not need to keep showing it.
  const passwordResetSuccess = Boolean(
    (location.state as { passwordResetSuccess?: boolean } | null)?.passwordResetSuccess
  );

  const userService = new UserService();

  const handleGoogleLogin = () => {
    console.log('Google login clicked - not yet implemented');
  };

  const handleToggleAuth = () => {
    setIsLogin(!isLogin);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        // Login user
        const response = await userService.login({ email, password });
        console.log('🔐 [LOGIN] Received JWT token from backend');
        onLogin(response.user, response.token);
      } else {
        // Register new user
        const response = await userService.register({ username, email, password });
        console.log('🔐 [REGISTER] Received JWT token from backend');
        onLogin(response.user, response.token);
      }
    } catch (err: any) {
      setError(err.message || t('auth.authFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginScreen}>
      <div className={styles.loginContainer}>
        <div className={styles.loginIcon}>🏋️</div>
        <h1 className={styles.loginTitle}>{t('auth.title')}</h1>
        <p className={styles.loginMessage}>{t('auth.startPrompt')}</p>
        <div className={styles.loginButtons}>
          <button 
            className={`${styles.loginButton} ${styles.loginButtonSecondary}`} 
            onClick={onDemoLogin}
            data-testid="demo-user-login"
          >
            {t('auth.demoLogin')}
          </button>

          <div className={styles.divider}>
            <span>{t('auth.divider')}</span>
          </div>

          <form onSubmit={handleSubmit} className={styles.authForm}>
            {passwordResetSuccess && (
              <div className={styles.successMessage} data-testid="password-reset-success-message">
                {t('auth.passwordResetSuccess')}
              </div>
            )}
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            {!isLogin && (
              <input
                type="text"
                placeholder={t('auth.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={styles.authInput}
                required
                disabled={isLoading}
                data-testid="username-input"
              />
            )}
            <input
              type="text"
              placeholder={t('auth.emailOrUsername')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.authInput}
              required
              disabled={isLoading}
              data-testid="email-input"
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.authInput}
              required
              disabled={isLoading}
              data-testid="password-input"
            />
            <button
              type="submit"
              className={`${styles.loginButton} ${styles.loginButtonPrimary}`}
              disabled={isLoading}
              data-testid="auth-submit-button"
            >
              {isLoading ? t('auth.pleaseWait') : (isLogin ? t('auth.login') : t('auth.register'))}
            </button>
          </form>

          {isLogin && (
            <Link to="/forgot-password" className={styles.toggleButton} data-testid="forgot-password-link">
              {t('auth.forgotPasswordLink')}
            </Link>
          )}

          <div className={styles.toggleAuth}>
            <span className={styles.toggleText}>
              {isLogin ? t('auth.toggleToRegisterPrompt') : t('auth.toggleToLoginPrompt')}
            </span>
            <button 
              type="button"
              onClick={handleToggleAuth}
              className={styles.toggleButton}
              data-testid="toggle-auth-button"
            >
              {isLogin ? t('auth.register') : t('auth.login')}
            </button>
          </div>

          <div className={styles.divider}>
            <span>{t('auth.or')}</span>
          </div>
          <button 
            className={`${styles.loginButton} ${styles.loginButtonGoogle} ${styles.disabledButton}`} 
            onClick={handleGoogleLogin}
            disabled
            data-testid="google-login-button"
          >
            <svg className={styles.googleIcon} viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.googleComingSoon')}
          </button>
        </div>
      </div>
    </div>
  );
};
